/**
 * 分镜工具条(两态收敛批,2026-07-26)—— 分镜阶段的**片级**信息与入口,挂在分镜网格上方。
 *
 * ═══ 装了什么、为什么 ═══════════════════════════════════════════════════════════
 * 1. **出场主体**:导演产的 subjects 注册表(`["@token: 外观描述"]`)。它是跨镜人物/物件
 *    一致性的**唯一机制**(文本锁:每镜 prompt 由编译层把 @token 展开成同一段描述 ——
 *    没有图像参考,靠逐字重复)。此前 UI 完全不显示,用户看到「一个30岁女教师」贯穿全片
 *    却找不到在哪改她的衣服。这里给 chips + popover 编辑描述;**token 名不可改**
 *    (panelSubjectRefs 按名引用,改名 = 全片断链)。
 * 2. **素材**:出分镜后页面级 ProductImagePanel 退场(videoSource.showProduct 收窄),
 *    产品图的改动入口收到这里 —— 并把「此刻改动只影响重新生成的首帧」写在入口旁边:
 *    成片不会自己变,不说清用户会以为换图 = 换片。
 * 3. **总长/密度摘要**:逐镜秒数之和 + 超密镜数(口播密度 > 5.5 字/秒会念糊)。
 */
import { ChevronDown, Package, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Textarea } from "@/shared/ui/textarea";
import { VOICEOVER_DENSITY_MAX, voiceoverDensity } from "@/shared/lib/video/cameraLexicon";
import { cn } from "@/shared/lib/utils";

import { parseSubjectEntry } from "../../model/subjectRegistry";
import type { PanelShotData } from "../../model/panelShot";

function SubjectChip({
  entry,
  onChange,
  disabled,
}: {
  entry: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const { token, desc } = parseSubjectEntry(entry);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(desc);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setDraft(desc);
        // 关闭即提交(有改动才写回;token 原样保留)
        else if (draft.trim() !== desc) onChange(`@${token}: ${draft.trim()}`);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="cursor-pointer rounded-full border border-primary/40 bg-primary/5 px-2.5 py-0.5 font-mono text-2xs text-primary transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
          title={desc}
        >
          @{token}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 p-3">
        <div className="font-mono text-xs font-medium">@{token}</div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          className="text-xs"
          placeholder="外观描述(30岁女教师,黑色马尾,米色风衣…)"
        />
        <p className="text-2xs leading-snug text-muted-foreground">
          每一镜都用这段描述锁同一个形象 —— 改这里 = 全片一起改;只对之后重新生成的首帧/成片生效。
          代号不可改(各镜按代号引用)。
        </p>
      </PopoverContent>
    </Popover>
  );
}

interface Props {
  /** 导演产的主体注册表;null/空 = 本片没有跨镜主体(单镜/纯产品片常见),整段不渲染 */
  subjects?: string[] | null;
  onSubjectsChange: (next: string[]) => void;
  shots: PanelShotData[];
  /** 素材面板(ProductImagePanel 原样嵌入;缺省 = 该来源无产品图,如海报/模板流) */
  productSlot?: ReactNode;
  disabled?: boolean;
}

export function StoryboardToolbar({
  subjects,
  onSubjectsChange,
  shots,
  productSlot,
  disabled,
}: Props) {
  const [matOpen, setMatOpen] = useState(false);
  const totalSec = shots.reduce((a, s) => a + (s.duration || 0), 0);
  const overCount = shots.filter(
    (s) => voiceoverDensity(s.voiceover, s.duration) > VOICEOVER_DENSITY_MAX
  ).length;
  const hasSubjects = !!subjects && subjects.length > 0;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {hasSubjects && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              出场主体
            </span>
            {subjects!.map((entry, i) => (
              <SubjectChip
                key={i}
                entry={entry}
                disabled={disabled}
                onChange={(next) => {
                  const arr = [...subjects!];
                  arr[i] = next;
                  onSubjectsChange(arr);
                }}
              />
            ))}
          </div>
        )}
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">
          总长 <span className="font-mono text-foreground">{totalSec}s</span> · {shots.length} 镜
          {overCount > 0 && (
            <span className={cn("ml-2 font-medium text-destructive")}>{overCount} 镜口播超密</span>
          )}
        </span>
      </div>

      {/**
       * 全片素材(2026-08-06 用户审阅后改口)。
       *
       * 原文是「📦 素材(产品/人物图)」+「此刻改动只影响重新生成的首帧」—— 用户看不懂它是干嘛的。
       * 真问题是**没说作用范围**:它管的是**整片共用**的产品图/人物图(哪一镜要用由引擎按
       * 镜头内容判),而「这一镜用什么动作 / 挂什么参考」是**逐镜**的事,点那一镜到右栏改。
       * 用户会问「这是哪一镜的?」正是因为这句没写。现在把范围写在标题里,并指路逐镜入口。
       * 同批:emoji→lucide、原生 details→Radix Collapsible(与页内其余折叠统一)。
       */}
      {productSlot && (
        <Collapsible
          open={matOpen}
          onOpenChange={setMatOpen}
          className="rounded-md border bg-background/60"
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left"
            >
              <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                <Package className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.8} />
                全片素材 · 产品 / 人物图
              </span>
              <span className="flex min-w-0 items-center gap-2 text-2xs text-muted-foreground">
                <span className="truncate">
                  整片共用(哪一镜用由 AI 按镜头内容判);逐镜的动作 / 参考:点下面那一镜,在右栏改
                </span>
                <span className="shrink-0 text-primary">{matOpen ? "收起" : "展开"}</span>
                <ChevronDown
                  className={cn("h-3 w-3 shrink-0 transition-transform", matOpen && "rotate-180")}
                />
              </span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t px-3 py-3">
            <p className="mb-2 text-2xs text-muted-foreground">
              改这里只影响<span className="font-medium text-foreground">之后</span>
              重新生成的台本图 —— 已出的图和已出的成片不会自己变。
            </p>
            {productSlot}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
